import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "@/lib/auth";
import { PODCAST_STUDIOS, STUDIO_CITIES } from "@/data/demo";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { HotBadge, LivePulse, SocialProof, LiveNudge, hotFor } from "@/components/Engagement";
import { toast } from "sonner";

const TIMES = ["09:00 AM", "11:00 AM", "01:00 PM", "03:00 PM", "05:00 PM", "07:00 PM"];

export default function Studios() {
  const { user, refreshUser } = useAuth();
  const [city, setCity] = useState("All");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("rating");
  const [active, setActive] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [hours, setHours] = useState(1);
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem("mfdx_studio_favs") || "[]"); } catch { return []; } });
  const [savedOnly, setSavedOnly] = useState(false);
  const toggleFav = (name) => setFavs((f) => { const n = f.includes(name) ? f.filter((x) => x !== name) : [...f, name]; localStorage.setItem("mfdx_studio_favs", JSON.stringify(n)); return n; });

  const load = useCallback(() => api.get("/studios/bookings").then((r) => setBookings(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const filtered = PODCAST_STUDIOS
    .filter((s) => (city === "All" || s.city === city))
    .filter((s) => !savedOnly || favs.includes(s.name))
    .filter((s) => {
      const q = query.trim().toLowerCase();
      return !q || s.name.toLowerCase().includes(q) || s.area.toLowerCase().includes(q) || s.city.toLowerCase().includes(q);
    })
    .sort((a, b) => sort === "price-asc" ? a.price - b.price : sort === "price-desc" ? b.price - a.price : b.rating - a.rating);
  const open = (s) => { setActive(s); setDate(""); setTime(""); setHours(1); };
  const total = active ? active.price * hours : 0;

  const confirm = async () => {
    if (!date || !time) return toast.error("Pick a date & time slot");
    try {
      await api.post("/studios/book", { studio: active.name, city: `${active.area}, ${active.city}`, date, time, hours, total });
      await refreshUser(); await load(); setActive(null);
      toast.success(`Booked ${active.name} · ₹${total.toLocaleString("en-IN")} deducted`);
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); }
  };

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Book a Podcast Studio" subtitle="Record reels, explainers & podcasts at pro studios anywhere in India — pay from your wallet." icon="Mic" color="#8B5CF6"
        action={<span className="bg-lime-50 text-lime-700 border border-lime-200 rounded-full px-3 py-1.5 text-sm font-semibold">Balance ₹{(user?.wallet ?? 0).toLocaleString("en-IN")}</span>} />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4" data-testid="studios-live-strip">
        <LivePulse label="18 MFDs booking studios now" />
        <SocialProof icon="Mic">1,200+ recordings this month</SocialProof>
        <SocialProof icon="Flame">Weekend slots filling fast</SocialProof>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="studio-search"
            placeholder="Search by studio name or locality…" className="w-full rounded-xl border border-gray-200 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-blue-400" />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="studio-sort"
          className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400 bg-white">
          <option value="rating">Top rated</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
        </select>
        <button onClick={() => setSavedOnly((v) => !v)} data-testid="studio-saved-toggle" className={`rounded-xl px-3 py-2.5 text-sm font-semibold border inline-flex items-center gap-1.5 whitespace-nowrap ${savedOnly ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}><Icon name="Heart" size={15} className={savedOnly ? "fill-white" : ""} /> Saved{favs.length ? ` (${favs.length})` : ""}</button>
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {STUDIO_CITIES.map((c) => (
          <button key={c} onClick={() => setCity(c)} data-testid={`studio-city-${c.replace(/\s/g, "-").toLowerCase()}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${city === c ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>{c}</button>
        ))}
      </div>

      {filtered.length === 0 && <div className="text-center py-12 text-gray-400" data-testid="studio-empty"><Icon name="SearchX" size={30} className="mx-auto mb-2" />No studios match your search.</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((s) => (
          <div key={s.name} className="card-mfx overflow-hidden hover-lift" data-testid="studio-card">
            <div className="h-40 bg-gray-100 overflow-hidden relative">
              <img src={s.img} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
              <span className="absolute top-3 left-3 bg-black/60 text-white text-xs font-semibold rounded-full px-2.5 py-1 flex items-center gap-1"><Icon name="MapPin" size={12} /> {s.city}</span>
              <button onClick={() => toggleFav(s.name)} data-testid="studio-fav-btn" className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 grid place-items-center hover:scale-110 transition-transform">
                <Icon name="Heart" size={16} className={favs.includes(s.name) ? "fill-red-500 text-red-500" : "text-gray-500"} />
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div><h3 className="font-head font-semibold">{s.name}</h3><div className="text-xs text-gray-400">{s.area}</div></div>
                <span className="flex items-center gap-1 text-amber-500 font-semibold text-sm shrink-0"><Icon name="Star" size={14} className="fill-amber-400" /> {s.rating}</span>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <HotBadge badge={hotFor(s.name).badge} />
                <SocialProof icon="CalendarCheck">Booked {hotFor(s.name).booked}× this month</SocialProof>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">{s.amenities.map((a) => <span key={a} className="text-[11px] bg-gray-100 rounded-full px-2 py-0.5 text-gray-500">{a}</span>)}</div>
              <div className="flex items-center justify-between mt-4">
                <div className="font-head font-bold text-lg text-blue-600">₹{s.price.toLocaleString("en-IN")}<span className="text-xs text-gray-400 font-normal">/hour</span></div>
                <Btn onClick={() => open(s)} data-testid="studio-book-btn"><Icon name="CalendarPlus" size={15} /> Book</Btn>
              </div>
            </div>
          </div>
        ))}
      </div>

      {bookings.length > 0 && (
        <Card className="mt-6">
          <h3 className="font-head font-semibold text-lg mb-4">Your studio bookings</h3>
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-100 p-4" data-testid="studio-booking-row">
                <div className="w-10 h-10 rounded-xl bg-violet-50 grid place-items-center"><Icon name="Mic" size={18} className="text-violet-500" /></div>
                <div className="min-w-0"><div className="font-medium">{b.studio}</div><div className="text-xs text-gray-400">{b.city}</div></div>
                <span className="text-xs bg-blue-50 text-blue-600 rounded-full px-2.5 py-1 font-semibold">{b.hours} hr</span>
                <div className="ml-auto text-sm font-medium">{b.date} · {b.time}</div>
                <div className="font-semibold">₹{b.total.toLocaleString("en-IN")}</div>
                <span className="text-[11px] bg-emerald-50 text-emerald-600 rounded-full px-2.5 py-0.5 font-semibold">{b.status}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {active && (
        <div className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setActive(null)}>
          <div className="w-full max-w-md card-mfx p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1"><h3 className="font-head font-bold text-lg">{active.name}</h3><button onClick={() => setActive(null)} className="text-gray-400"><Icon name="X" size={20} /></button></div>
            <p className="text-sm text-gray-500 mb-4">{active.area}, {active.city} · ₹{active.price.toLocaleString("en-IN")}/hour</p>
            <label className="text-sm font-semibold">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="studio-date" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400" />
            <label className="text-sm font-semibold mt-4 block">Time slot</label>
            <div className="flex flex-wrap gap-2 mt-2">{TIMES.map((t) => <button key={t} onClick={() => setTime(t)} data-testid="studio-time-slot" className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${time === t ? "bg-blue-600 text-white border-transparent" : "border-gray-200 text-gray-600"}`}>{t}</button>)}</div>
            <label className="text-sm font-semibold mt-4 block">Duration</label>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={() => setHours(Math.max(1, hours - 1))} className="w-9 h-9 rounded-xl border border-gray-200 grid place-items-center" data-testid="studio-hours-minus"><Icon name="Minus" size={16} /></button>
              <span className="font-head font-bold text-lg w-16 text-center" data-testid="studio-hours">{hours} hr</span>
              <button onClick={() => setHours(Math.min(8, hours + 1))} className="w-9 h-9 rounded-xl border border-gray-200 grid place-items-center" data-testid="studio-hours-plus"><Icon name="Plus" size={16} /></button>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3 mt-5">
              <span className="text-sm text-gray-500">Total</span><span className="font-head font-bold text-xl text-blue-600" data-testid="studio-total">₹{total.toLocaleString("en-IN")}</span>
            </div>
            <Btn onClick={confirm} className="mt-4 w-full justify-center" data-testid="studio-confirm-btn"><Icon name="CheckCircle2" size={16} /> Confirm & pay from wallet</Btn>
          </div>
        </div>
      )}

      <LiveNudge id="studios" title="Studios book out fast" message="Prime weekend recording slots go quickly. Reserve your time before they're gone." ctaLabel="Got it" />
    </div>
  );
}
